import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMyNotifPrefs, setMyNotifPrefs, type NotifPrefCategory, type NotifPrefs } from '../api';
import { toast } from '../toast';

const ROWS: { key: NotifPrefCategory; label: string; desc: string }[] = [
  { key: 'bet', label: 'Bets', desc: 'Invites, acceptances, cancellations, results.' },
  { key: 'match', label: 'Match kickoff & FT', desc: 'Owned teams: when matches start and end.' },
  { key: 'goal', label: 'Goals', desc: 'Live score changes during owned-team matches.' },
  { key: 'team', label: 'Team advancement', desc: 'When your teams advance a round.' },
  { key: 'digest', label: 'Daily digest', desc: 'Morning roundup at 08:30 Riga.' },
  { key: 'announcement', label: 'Announcements', desc: 'Admin broadcasts.' },
  { key: 'news', label: 'News', desc: 'Currently off by default; off feed-wide.' },
];

export default function NotifPrefsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['notif-prefs'], queryFn: getMyNotifPrefs });
  const mut = useMutation({
    mutationFn: setMyNotifPrefs,
    onSuccess: (res) => {
      qc.setQueryData(['notif-prefs'], { prefs: res.prefs, defaults: data?.defaults });
    },
    onError: () => toast.error('Could not save preference.'),
  });

  if (isLoading || !data) {
    return (
      <section className="card p-3">
        <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Notification types</h2>
        <div className="text-xs text-dim">Loading…</div>
      </section>
    );
  }

  const prefs: NotifPrefs = data.prefs;
  function toggle(k: NotifPrefCategory, v: boolean) {
    qc.setQueryData(['notif-prefs'], { ...data, prefs: { ...prefs, [k]: v } });
    mut.mutate({ [k]: v });
  }

  return (
    <section className="card p-3 space-y-2">
      <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Notification types</h2>
      <p className="text-[10px] text-dim">Mute individual event categories. Applies to push only — feed/bell still records everything.</p>
      {ROWS.map((r) => (
        <label key={r.key} className="flex items-center justify-between gap-3 py-1 cursor-pointer">
          <div className="flex-1 min-w-0">
            <div className="text-sm">{r.label}</div>
            <div className="text-[10px] text-dim">{r.desc}</div>
          </div>
          <span
            role="switch" aria-checked={!!prefs[r.key]}
            onClick={() => toggle(r.key, !prefs[r.key])}
            className={`shrink-0 inline-flex items-center w-10 h-6 rounded-full p-0.5 transition-colors ${prefs[r.key] ? 'bg-pitch-500' : 'bg-ink-700'}`}
          >
            <span className={`block w-5 h-5 rounded-full bg-chalk transition-transform ${prefs[r.key] ? 'translate-x-4' : ''}`} />
          </span>
        </label>
      ))}
    </section>
  );
}
